import { CommonModule } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Auth } from '@freelancer/auth';
import { Datastore } from '@freelancer/datastore';
import type {
  TrackingConsent,
  UserConsent,
  UserConsentCollection,
} from '@freelancer/datastore/collections';
import { DatastoreUserConsentModule } from '@freelancer/datastore/collections';
import { LocalStorage } from '@freelancer/local-storage';
import { Location } from '@freelancer/location';
import { Tracking, TrackingModule } from '@freelancer/tracking';
import { UiModule } from '@freelancer/ui';
import { ButtonColor } from '@freelancer/ui/button';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { LinkColor, LinkUnderline } from '@freelancer/ui/link';
import { TextSize } from '@freelancer/ui/text';
import { isDefined } from '@freelancer/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { from, Observable, of, switchMap } from 'rxjs';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  Subscription,
} from 'rxjs';
import {
  distinctUntilChanged,
  map,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import { TrackingConsentBannerService } from './tracking-consent-banner.service';

@UntilDestroy({ className: 'TrackingConsentBanner' })
@Component({
  standalone: true,
  imports: [CommonModule, DatastoreUserConsentModule, TrackingModule, UiModule],
  selector: 'app-tracking-consent-banner',
  template: `
    @if (shouldShowBanner$ | flAsync) {
      <div
        class="TrackingConsentBanner"
        flTrackingSection="TrackingConsentBanner"
      >
        <div class="TrackingConsentBanner-content">
          <p class="text-sm text-foreground mb-small">
            <ng-container i18n="Tracking consent banner text">
              We use cookies and similar technologies to understand how you use
              our site, optimize its functionality, to create more valuable
              experiences for you and deliver advertising and other content
              tailored to your interests. Learn more in our
            </ng-container>
            <fl-link
              i18n="Tracking consent banner privacy policy link"
              flTrackingLabel="TrackingConsentBanner-PrivacyPolicyLink"
              [color]="LinkColor.FOREGROUND"
              [link]="'/about/privacy'"
              [newTab]="true"
              [size]="TextSize.BODY_16"
              [underline]="LinkUnderline.ALWAYS"
            >
              Privacy Policy</fl-link
            >.
          </p>
          <div>
            <fl-button
              class="mr-small tablet:mr-mid"
              i18n="Tracking consent banner accept button"
              [color]="ButtonColor.PRIMARY"
              [flTrackingLabel]="'TrackingConsentBanner-AcceptButton'"
              (click)="handleUserDecision(true, 'cta')"
            >
              Accept cookies
            </fl-button>
            <fl-link
              i18n="Tracking consent banner reject link"
              flTrackingLabel="TrackingConsentBanner-RejectLink"
              [color]="LinkColor.FOREGROUND"
              [size]="TextSize.BODY_16"
              [underline]="LinkUnderline.ALWAYS"
              (click)="handleUserDecision(false, 'cta')"
            >
              Reject cookies
            </fl-link>
          </div>
        </div>
        <fl-icon
          class="TrackingConsentBanner-closeIcon"
          flTrackingLabel="TrackingConsentBanner-CloseIcon"
          label="Close icon"
          i18n-label="Close icon label"
          [color]="IconColor.FOREGROUND"
          [name]="'ui-close'"
          [size]="IconSize.MID"
          [hoverAnimation]="'spin-and-highlight'"
          (click)="handleUserDecision(false, 'icon')"
          [useIconFont]="true"
        />
      </div>
    }
  `,
  styleUrls: ['./tracking-consent-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrackingConsentBannerComponent implements OnInit, OnDestroy {
  private auth = inject(Auth);
  private datastore = inject(Datastore);
  private localStorage = inject(LocalStorage);
  private location = inject(Location);
  private trackingConsentBannerService = inject(TrackingConsentBannerService);
  private tracking = inject(Tracking);

  ButtonColor = ButtonColor;
  IconColor = IconColor;
  IconSize = IconSize;
  LinkColor = LinkColor;
  LinkUnderline = LinkUnderline;
  TextSize = TextSize;

  isLoggedIn$: Observable<boolean>;
  private shouldShowBannerSubject$ = new BehaviorSubject<boolean>(false);
  shouldShowBanner$ = this.shouldShowBannerSubject$.asObservable();
  private syncedBannerStateSubject$ = new BehaviorSubject<boolean>(false);
  syncedBannerState$ = this.syncedBannerStateSubject$.asObservable();
  userConsent$: Observable<UserConsent | undefined>;

  private subscriptions = new Subscription();

  ngOnInit(): void {
    this.isLoggedIn$ = this.auth.isLoggedIn();

    this.userConsent$ = this.datastore
      .collection<UserConsentCollection>('userConsent', query =>
        this.isLoggedIn$.pipe(
          map(isLoggedIn =>
            isLoggedIn
              ? query
                  .where('dataType', '==', 'tracking')
                  .where('usageType', '==', 'marketing')
                  .limit(1)
              : query.null(),
          ),
        ),
      )
      .valueChanges()
      .pipe(map(consents => (consents.length ? consents[0] : undefined)));

    /**
     * Check if the tracking consent banner is supported or logged in user has consented previously
     * even if the banner is not supported (to ensure that we still respect the user's consent)
     */
    this.subscriptions.add(
      from(this.trackingConsentBannerService.isSupported())
        .pipe(
          switchMap(isSupported => {
            if (isSupported) {
              return of(true);
            }
            return this.userConsent$.pipe(
              map(consent => isDefined(consent)),
              take(1),
            );
          }),
          take(1),
        )
        .subscribe(shouldInit => {
          if (shouldInit) {
            this.initializeBanner();
          } else {
            this.shouldShowBannerSubject$.next(false);
          }
        }),
    );
  }

  private initializeBanner(): void {
    // On log out, clear logged out tracking consent
    this.auth.registerBeforeLogoutAction(async () => {
      this.localStorage.set('trackingConsent', null, true);
    });

    // Enable consent mode
    this.trackingConsentBannerService.setBingAdsDefaultConsentSetting();

    // Sync the banner state with user consent and local storage tracking consent
    this.subscriptions.add(
      combineLatest([
        this.userConsent$,
        this.localStorage.get('trackingConsent'),
        this.localStorage.get('trackingConsent', true),
      ])
        .pipe(take(1))
        .subscribe(
          ([
            userConsent,
            loggedInTrackingConsent,
            loggedOutTrackingConsent,
          ]) => {
            this.syncBannerState(
              userConsent,
              loggedInTrackingConsent,
              loggedOutTrackingConsent,
            );
          },
        ),
    );

    // Check if the banner should be shown based on synced state and local storage tracking consent
    this.subscriptions.add(
      combineLatest([
        this.syncedBannerState$,
        this.localStorage.get('trackingConsent', true),
        this.localStorage.get('trackingConsent', false),
      ])
        .pipe(
          withLatestFrom(this.isLoggedIn$),
          map(
            ([
              [
                syncedBannerState,
                loggedOutTrackingConsent,
                loggedInTrackingConsent,
              ],
              isLoggedIn,
            ]) => {
              if (
                // If banner state is not yet synced, do not show banner
                !syncedBannerState ||
                // If logged in and either consent exists, do not show banner
                // This is to catch the case where the banner is showing while the user is logging in
                // because the logged in consent has not yet been synced
                (isLoggedIn &&
                  (isDefined(loggedOutTrackingConsent) ||
                    isDefined(loggedInTrackingConsent))) ||
                // If logged out and logged-out consent exists, do not show banner
                (!isLoggedIn && isDefined(loggedOutTrackingConsent)) ||
                // If logging out, do not show banner
                this.location.url.includes('logout')
              ) {
                return false;
              }
              // Otherwise, show banner
              return true;
            },
          ),
          distinctUntilChanged(),
        )
        .subscribe(shouldShow => {
          this.shouldShowBannerSubject$.next(shouldShow);

          if (shouldShow) {
            this.tracking.trackCustomEvent(
              'TrackingConsentBanner-BannerDisplayed',
              'TrackingConsentBanner',
            );
          }
        }),
    );

    // Update ad_storage consent setting when the user provides or denies consent
    this.subscriptions.add(
      this.localStorage
        .get('trackingConsent')
        .subscribe(localStorageTrackingConsent => {
          this.trackingConsentBannerService.updateBingAdsConsentSetting(
            localStorageTrackingConsent?.consent,
          );
        }),
    );
  }

  async syncBannerState(
    userConsent: UserConsent | undefined,
    loggedInTrackingConsent: TrackingConsent | null | undefined,
    loggedOutTrackingConsent: TrackingConsent | null | undefined,
  ): Promise<void> {
    const isLoggedIn = await firstValueFrom(
      this.isLoggedIn$.pipe(untilDestroyed(this)),
    );

    if (isLoggedIn) {
      const candidates: TrackingConsent[] = [];

      if (isDefined(loggedOutTrackingConsent)) {
        candidates.push(loggedOutTrackingConsent);
      }

      if (isDefined(loggedInTrackingConsent)) {
        candidates.push(loggedInTrackingConsent);
      }

      if (isDefined(userConsent)) {
        candidates.push({
          consent: userConsent.consentGiven,
          timestamp: userConsent.dateCreated,
        } as TrackingConsent);
      }

      if (candidates.length === 0) {
        // If no consent anywhere, ask for consent
        this.localStorage.set('trackingConsent', null);
        this.syncedBannerStateSubject$.next(true);
        return;
      }

      const mostRecent = candidates.sort(
        (a, b) => b.timestamp - a.timestamp,
      )[0];

      if (isDefined(userConsent)) {
        if (
          mostRecent.timestamp > userConsent.dateCreated &&
          mostRecent.consent !== userConsent.consentGiven
        ) {
          // If most recent consent is newer and has different consent, create new DB consent
          await this.createUserConsent(mostRecent);
          this.localStorage.set('trackingConsent', mostRecent);
          this.syncedBannerStateSubject$.next(true);
          return;
        }

        if (userConsent.consentGiven === mostRecent.consent) {
          // If same consent (regardless of timestamp), trust DB’s timestamp
          this.localStorage.set('trackingConsent', {
            consent: userConsent.consentGiven,
            timestamp: userConsent.dateCreated,
          });
          this.syncedBannerStateSubject$.next(true);
          return;
        }
      }

      // If no DB consent, save most recent (logged in or logged out) to DB and local storage
      await this.createUserConsent(mostRecent);
      this.localStorage.set('trackingConsent', mostRecent);
      this.syncedBannerStateSubject$.next(true);

      return;
    }

    // If logged out user has logged out consent in local storage, do nothing
    if (isDefined(loggedOutTrackingConsent)) {
      this.syncedBannerStateSubject$.next(true);
      return;
    }

    // If logged out consent in local storage, ask for consent
    this.localStorage.set('trackingConsent', null);
    this.syncedBannerStateSubject$.next(true);
  }

  async createUserConsent(trackingConsent: TrackingConsent): Promise<void> {
    this.datastore.createDocument<UserConsentCollection>('userConsent', {
      dataType: 'tracking',
      usageType: 'marketing',
      collectionLocation: 'consent_banner',
      consentGiven: trackingConsent.consent,
      dateCreated: trackingConsent.timestamp,
    });
  }

  async handleUserDecision(
    decision: boolean,
    clicked: 'cta' | 'icon',
  ): Promise<void> {
    const isLoggedIn = await firstValueFrom(
      this.isLoggedIn$.pipe(untilDestroyed(this)),
    );
    const now = Date.now();
    if (isLoggedIn) {
      // Catch the case where `trackingConsent` is deleted from local storage
      // Banner is reopened, and user clicks accept or reject
      const userConsent = await firstValueFrom(
        this.userConsent$.pipe(untilDestroyed(this)),
      );
      if (!isDefined(userConsent)) {
        await this.createUserConsent({
          consent: decision,
          timestamp: now,
        });
      } else if (
        now > userConsent.dateCreated &&
        decision !== userConsent.consentGiven
      ) {
        // Check if consent is different and more recent
        await this.createUserConsent({
          consent: decision,
          timestamp: now,
        });
      } else {
        // User clicked the same consent decision, get the timestamp from existing consent for local storage
        this.localStorage.set('trackingConsent', {
          consent: userConsent.consentGiven,
          timestamp: userConsent.dateCreated,
        });
        return;
      }
    }
    // Set local storage consent for logged out users or logged in users who just provided consent
    this.localStorage.set('trackingConsent', {
      consent: decision,
      timestamp: now,
    });

    this.tracking.trackCustomEvent(
      'TrackingConsentBanner-UserDecision',
      'TrackingConsentBanner',
      {
        value: decision ? 'accepted' : 'rejected',
        clicked,
      },
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
